import { REACT_APP_API_QUOTATION } from '../config';
import { coalesceGet } from './coalesceGet';
import { handleServiceError } from './errorHandler';
import { getHeaders } from './middleware';
import { sentryFetch } from './sentryApiWrapper';
import { monitorInsuranceAPI, monitorQuoteCalculation } from '../utils/sentry';

const BASE_URL = REACT_APP_API_QUOTATION;

export interface LeadAdditionalData {
  audience?: string;
  has_nomad_account?: string;
}

export interface Lead {
  first_name: string;
  middle_name?: string;
  last_name: string;
  second_last_name?: string;
  date_of_birth: string;
  email: string;
  comments?: string;
  additional_data?: LeadAdditionalData;
}

export interface IncomeReplacement {
  code: string;
  temporary_total_disability: number;
  permanent_disability: number;
  rider_optional_total_ptd: number;
  rider_optional_total_ttd: number;
  total_income_replacement: number;
  total_income_replacement_in_frecuency: number;
}

export interface Protection {
  amount: number;
  critical_type: string;
  total_ci_in_frecuency: number;
  total_ci: number;
  total_cp_in_frecuency: number;
  total_cp: number;
  type: string;
}

export interface Quotation {
  frecuency: string;
  term: string;
  apply_income_replacement: boolean;
  coverage_premium: number;
  coverage_premium_in_frecuency: number;
  substandard: number;
  substandard_in_frecuency: number;
  rider_optional_total_add: number;
  rider_optional_total_add_in_frecuency: number;
  rider_optional_total_ad?: number;
  rider_optional_total_ad_in_frecuency?: number;
  rider_optional_total_tpd?: number;
  rider_optional_total_tpd_in_frecuency?: number;
  rider_optional_total_rop: number;
  rider_optional_total_rop_in_frecuency: number;
  rider_optional_total_ptd: number;
  rider_optional_total_ttd: number;
  total_income_replacement: number;
  total_income_replacement_in_frecuency: number;
  total_premium: number;
  total_premium_in_frecuency: number;
  rop_amount: number;
  diff_percentage_annual: number;
  income_replacement: IncomeReplacement;
  income_replacements: Array<IncomeReplacement>;
  protection: Protection;
  protections: Array<Protection>;
  selected: boolean;
}

export interface QuoteRequest {
  version: string;
  product_type: string;
  country: string;
  country_origin: string;
  term: string;
  frecuency: string;
  gender: string;
  smoker: boolean;
  age: number;
  policy_amount: number;
  add_amount: number | null;
  ad_amount?: number | null;
  tpd_amount?: number | null;
  rop: boolean;
  flat_extra_years: number;
  flat_extra_amount: number;
  extra_premium_table_rating: number;
  extra_premium_table_add: number;
  extra_premium_table_ad?: number;
  annual_income: number;
  protection: Protection | null;
  ci_protections?: Protection[];
  cp_protections?: Protection[];
  income_replacement: {
    code: string;
  }
  lead: Lead;
  frecuency_selected: string;
  term_selected: string;
  coverage_include_amount?: number;
  score?: number;
  is_test?: boolean;
}

interface Promotion {
  qualified: boolean;
  promotion_code: string;
  free_installments: number;
}

export interface QuotationResponse {
  id: number;
  id_quotation?: string;
  id_quotation_engine: number;
  product_type: string;
  currency: string;
  country: string;
  country_origin: string;
  version: string;
  frecuency: string;
  term: string;
  smoker: boolean;
  gender: string;
  age: number;
  insured_amount: number;
  insured_add_amount: number;
  insured_ad_amount?: number;
  insured_tpd_amount?: number;
  rider_optional_total_ad?: number;
  rider_optional_total_ad_in_frecuency?: number;
  rider_optional_total_tpd?: number;
  rider_optional_total_tpd_in_frecuency?: number;
  flat_extra_years: number;
  flat_extra_amount: number;
  extra_premium_table_rating: number;
  extra_premium_table_add: number;
  rop: boolean;
  annual_income: number;
  income_replacement: IncomeReplacement;
  protection: Protection | null;
  lead: Lead;
  quotations: Array<Quotation>;
  coverage_include_amount: number;
  score?: number;
  is_test?: boolean;
  promotion?: Promotion;
  created_at?: string;
}

export const getPreviousQuote = async (quoteId: string, apiKey: string): Promise<QuotationResponse> => {
  // In-flight only. A stored body would make refreshQuote reuse the pre-upsert quote.
  return coalesceGet(`quote:${quoteId}`, async () => {
    try {
      const response = await sentryFetch(
        `${BASE_URL}?token=${quoteId}`,
        {
          method: 'GET',
          headers: getHeaders(apiKey),
        },
        {
          operation: 'get_previous_quote',
          quoteId,
          apiKey
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Error: ${response.status} - ${errorData.message}`);
      }

      return await response.json();
      // eslint-disable-next-line
    } catch (error) {
      handleServiceError(error);
      throw error;
    }
  }, { ttlMs: 0 });
}

export const upsert = async (quoteID: string, quoteRequest: QuoteRequest, apiKey: string): Promise<QuotationResponse> => {
  return monitorInsuranceAPI(
    `${BASE_URL}upsert?token=${quoteID}`,
    'POST',
    'upsert_quote',
    async () => {
      const response = await sentryFetch(
        `${BASE_URL}upsert?token=${quoteID}`,
        {
          method: 'POST',
          headers: getHeaders(apiKey),
          body: JSON.stringify(quoteRequest),
        },
        {
          operation: 'upsert_quote',
          quoteID,
          product_type: quoteRequest.product_type,
          country: quoteRequest.country,
          policy_amount: quoteRequest.policy_amount,
          age: quoteRequest.age,
          smoker: quoteRequest.smoker
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Error: ${response.status} - ${errorData.message}`);
      }

      return await response.json();
    }
  );
};

export const calcule = async (quoteID: string, quoteRequest: QuoteRequest, apiKey: string): Promise<QuotationResponse> => {
  return monitorQuoteCalculation(
    `${quoteRequest.product_type}_${quoteRequest.country}`,
    quoteRequest,
    async () => {
      const response = await sentryFetch(
        `${BASE_URL}`,
        {
          method: 'POST',
          headers: getHeaders(apiKey),
          body: JSON.stringify(quoteRequest),
        },
        {
          operation: 'calculate_quote',
          quoteID,
          product_type: quoteRequest.product_type,
          country: quoteRequest.country,
          policy_amount: quoteRequest.policy_amount,
          age: quoteRequest.age,
          smoker: quoteRequest.smoker,
          term: quoteRequest.term,
          frecuency: quoteRequest.frecuency
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Error: ${response.status} - ${errorData.message}`);
      }

      return await response.json();
    }
  );
};
